import type { ReactNode } from 'react'

import { Trans } from '@lingui/react'
import { isNil } from 'ramda'

import type { Document, WebDocument } from '@twisto/api-graphql'
import { TextButton } from '@twisto/components/atoms/button'
import { Container } from '@twisto/components/atoms/container'
import {
  Headline,
  HeadlineIcon,
  HeadlineTitle,
} from '@twisto/components/atoms/headline'
import {
  SelectableListItemLink,
  SelectableListItemText,
} from '@twisto/components/molecules/selectable-list'
import { BigContractIcon } from '@twisto/icons'
import { styleUtils } from '@twisto/styles'

import { ContractDocumentDisclaimer } from './contract-document-disclaimer'
import * as styles from './contract-documents.styles'

type DocumentItem = Omit<Document, 'html'>

type Props = {
  documents: DocumentItem[] | WebDocument[]
  loading?: boolean
  onSubmit: () => void
  submitLabel?: ReactNode
}

export const ContractDocuments = ({
  documents,
  loading = false,
  onSubmit,
  submitLabel,
}: Props) => {
  const documentItems = documents.map((document) => {
    let url

    switch (document.__typename) {
      case 'Document':
        url = document.pdf
        break
      case 'WebDocument':
        url = document.url
        break
    }

    return {
      ...document,
      pdf: url,
    }
  })

  return (
    <Container
      flex
      css={styles.root}
      data-testid="contract-documents"
      size="tiny"
    >
      <Headline css={styleUtils.margin.bottom.xl}>
        <HeadlineIcon>
          <BigContractIcon
            color="primary"
            css={styleUtils.margin.bottom.xl}
            size="80"
          />
        </HeadlineIcon>
        <HeadlineTitle>
          <Trans id="flow.components.contractDocuments.title" />
        </HeadlineTitle>
      </Headline>
      <>
        {!isNil(documentItems) &&
          documentItems.map((document) => (
            <div
              key={document.pdf}
              css={[styleUtils.margin.bottom.lg, styleUtils.width.full]}
            >
              <SelectableListItemLink noMargin href={document.pdf}>
                <SelectableListItemText align="start">
                  {document.title}
                </SelectableListItemText>
              </SelectableListItemLink>
            </div>
          ))}
      </>
      <TextButton
        fullWidth
        color="primary"
        css={styles.submitButton}
        disabled={loading}
        onClick={onSubmit}
      >
        {submitLabel ?? (
          <Trans id="flow.components.contractDocuments.submitButton" />
        )}
      </TextButton>
      <ContractDocumentDisclaimer />
    </Container>
  )
}
